/**
 * The label that tells a tester which release of the test site they are looking at (#693).
 *
 * The person who releases names the batch on the staging dispatch (`release_label`, in
 * `.github/workflows/ci.yml`). The workflow's guard checks it and hands it to the web build as
 * `VITE_RELEASE_LABEL`; the build adds `VITE_RELEASE_COMMIT`, the short commit it was made from.
 *
 *   no label given, or not "Batch <number>"  → nothing is shown: no box, no word, no "undefined"
 *   a label                                  → "Test site · Batch 12", and the commit in small print
 *   a build that is not "every section on"   → nothing, whatever it was given: production is built
 *                                              Roster-only (`VITE_ENABLED_SECTIONS=roster`)
 *   staging's build (ADR-024, #725)          → "Staging · Batch 12" and the commit. Staging is built
 *                                              Roster-only like production, and is told it is staging
 *                                              by `VITE_PRODUCTION_COPY=1`, which only its own job sets
 *
 * THE RULE THAT CHANGED ON PURPOSE (ADR-024 section 2). Before #725 no Roster-only build could carry a
 * label. Staging is Roster-only and must show which batch it holds, so a Roster-only build may now
 * carry one, but only with `VITE_PRODUCTION_COPY=1`, and then it says "Staging", never "Test site".
 * Production's job never sets that, and its release check (`check-production-bundle.mjs
 * --require-roster-only`) refuses a bundle that was given a label or the setting.
 *
 * THIS FILE HAS NO IMPORTS AT RUN TIME, because `vite.config.ts` reads it too: the build drops a
 * label from any build that was given `VITE_ENABLED_SECTIONS`, so a production bundle never holds one.
 * The app then refuses it a second time on the page. Either alone would be enough; both are kept
 * because a leaked staging label on production would tell staff they are on a test site.
 */

/**
 * Exactly the word, a space, and a whole number from 1 to 9999. The staging guard holds the same rule
 * (`RELEASE_LABEL_PATTERN` in scripts/staging-policy.mjs, which runs before `npm ci` and so cannot
 * import this); tests/release-label.test.ts feeds both the same values.
 */
export const RELEASE_LABEL_PATTERN = /^Batch [1-9][0-9]{0,3}$/;

/** What a short commit looks like: lower-case hexadecimal, seven to twelve characters. */
export const RELEASE_COMMIT_PATTERN = /^[0-9a-f]{7,12}$/;

/** The words shown before the batch. */
export const TEST_SITE_WORDS = 'Test site';
/** The words shown before the batch on staging, the Roster-only copy of production (ADR-024). */
export const STAGING_WORDS = 'Staging';
/** The one value of `VITE_PRODUCTION_COPY` that makes a build staging's. Anything else is not. */
export const PRODUCTION_COPY_VALUE = '1';

/** What the shell draws. `railText` is for the icons-only menu, which has room for a word and the batch: "Test B12", "Staging B12" (Julio, 2026-10-16: a bare "B12" was taken for staging). */
export interface ReleaseLabel {
  readonly text: string;
  readonly railText: string;
  /** The short commit, when the build has a valid one. */
  readonly detail?: string;
  /** Everything in one sentence, for the tooltip and for a screen reader. */
  readonly fullText: string;
}

/** The label as given, or `null`. Anything that is not exactly "Batch <number>" is `null`. */
export function parseReleaseLabel(raw: string | undefined | null): string | null {
  return typeof raw === 'string' && RELEASE_LABEL_PATTERN.test(raw) ? raw : null;
}

/** The short commit as given, or `null`. */
export function parseReleaseCommit(raw: string | undefined | null): string | null {
  return typeof raw === 'string' && RELEASE_COMMIT_PATTERN.test(raw) ? raw : null;
}

/** Whether a build was made as staging's (ADR-024): exactly `VITE_PRODUCTION_COPY=1`. */
export function isProductionCopy(raw: string | undefined | null): boolean {
  return raw === PRODUCTION_COPY_VALUE;
}

/**
 * What to show. `allSectionsOn` is the app's own reading of its sections setting; `productionCopy` is
 * whether the build was made as staging's. A label shows on two kinds of build only:
 *
 *   every section on, not staging's  → the test site: "Test site · Batch N"
 *   not every section on, staging's  → staging: "Staging · Batch N"
 *
 * Every other build shows nothing. A Roster-only build that is not staging's is production. A build
 * marked as staging's that shows every section is a mistake, and either name on it would be wrong.
 */
export function buildReleaseLabel(input: {
  readonly label: string | undefined | null;
  readonly commit: string | undefined | null;
  readonly allSectionsOn: boolean;
  readonly productionCopy?: boolean;
}): ReleaseLabel | null {
  const productionCopy = input.productionCopy === true;
  if (input.allSectionsOn === productionCopy) return null;
  const label = parseReleaseLabel(input.label);
  if (label === null) return null;
  const commit = parseReleaseCommit(input.commit);
  const text = `${productionCopy ? STAGING_WORDS : TEST_SITE_WORDS} · ${label}`;
  return {
    text,
    railText: `${productionCopy ? STAGING_WORDS : 'Test'} B${label.slice('Batch '.length)}`,
    ...(commit === null ? {} : { detail: commit }),
    fullText: commit === null ? text : `${text}, commit ${commit}`,
  };
}

/**
 * What a build bakes in, as `vite.config.ts` calls it. The label survives a build that was given NO
 * `VITE_ENABLED_SECTIONS` at all (the test site, local work, tests), and staging's build, which is
 * given both `VITE_ENABLED_SECTIONS` and `VITE_PRODUCTION_COPY=1` (ADR-024). Production gives the
 * first and never the second, so its build bakes in no label.
 * A value that is not a label becomes the empty string, which the app reads as "no label".
 */
export function bakedReleaseLabel(env: {
  readonly VITE_RELEASE_LABEL?: string;
  readonly VITE_ENABLED_SECTIONS?: string;
  readonly VITE_PRODUCTION_COPY?: string;
}): string {
  if (env.VITE_ENABLED_SECTIONS !== undefined && !isProductionCopy(env.VITE_PRODUCTION_COPY)) return '';
  return parseReleaseLabel(env.VITE_RELEASE_LABEL) ?? '';
}

/** What a build bakes in for `VITE_PRODUCTION_COPY`: exactly `1` for staging's build, otherwise empty. */
export function bakedProductionCopy(env: { readonly VITE_PRODUCTION_COPY?: string }): string {
  return isProductionCopy(env.VITE_PRODUCTION_COPY) ? PRODUCTION_COPY_VALUE : '';
}
